import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { apiFetch } from "@/lib/api";
import {
  AUTH_QUERY_KEY,
  clearUserQueries,
  clearUserQueriesForIdentityChange,
} from "@/lib/auth-cache";
import {
  MAINTENANCE_REFRESH_INTERVAL_MS,
  PUBLIC_CONFIG_QUERY_KEY,
  shouldRevalidateSession,
  type PublicConfig,
} from "@/lib/maintenance";

export interface User {
  userId: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  roles: string[];
  sessionId: string;
}
interface AuthState {
  user: User | null;
  loading: boolean;
  publicConfig: PublicConfig | null;
  publicConfigLoading: boolean;
  maintenanceEnabled: boolean;
  restoringSession: boolean;
  refresh: (resetUserQueries?: boolean) => Promise<void>;
  refreshPublicConfig: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const previousMaintenance = useRef<boolean | undefined>(undefined);
  const restorationGeneration = useRef(0);
  const [restoringSession, setRestoringSession] = useState(false);
  useEffect(() => {
    sessionStorage.removeItem("preptrack-demo");
  }, []);
  const publicConfigQuery = useQuery({
    queryKey: PUBLIC_CONFIG_QUERY_KEY,
    queryFn: () => apiFetch<PublicConfig>("/public/config"),
    retry: false,
    staleTime: MAINTENANCE_REFRESH_INTERVAL_MS,
    refetchInterval: MAINTENANCE_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: true,
  });
  const query = useQuery<{ user: User | null }>({
    queryKey: AUTH_QUERY_KEY,
    queryFn: async () => {
      const data = await apiFetch<{ user: User }>("/auth/me");
      const previousUserId = queryClient
        .getQueryData<{ user: User | null }>(AUTH_QUERY_KEY)?.user?.userId;
      clearUserQueriesForIdentityChange(queryClient, previousUserId, data.user.userId);
      return data;
    },
    retry: false,
    staleTime: 60_000,
  });
  const refresh = async (resetUserQueries = false) => {
    const data = await apiFetch<{ user: User }>("/auth/me");
    const previousUserId = queryClient
      .getQueryData<{ user: User | null }>(AUTH_QUERY_KEY)?.user?.userId;
    if (resetUserQueries) {
      clearUserQueries(queryClient);
    } else {
      clearUserQueriesForIdentityChange(queryClient, previousUserId, data.user.userId);
    }
    queryClient.setQueryData(AUTH_QUERY_KEY, data);
  };
  const refreshPublicConfig = async () => {
    await publicConfigQuery.refetch();
  };
  const logout = async () => {
    await apiFetch("/auth/logout", { method: "POST" });
    clearUserQueries(queryClient);
    queryClient.setQueryData(AUTH_QUERY_KEY, { user: null });
  };
  const maintenanceSetting = publicConfigQuery.data?.maintenanceEnabled;
  const maintenanceWasObserved = query.error?.message === "maintenance_mode"
    ? true
    : previousMaintenance.current;
  const sessionRevalidationRequired = maintenanceSetting !== undefined
    && shouldRevalidateSession(maintenanceWasObserved, maintenanceSetting, query.data?.user?.roles);
  useEffect(() => {
    if (maintenanceSetting === undefined) return;
    const wasEnabled = query.error?.message === "maintenance_mode"
      ? true
      : previousMaintenance.current;
    if (maintenanceSetting) {
      previousMaintenance.current = true;
      restorationGeneration.current += 1;
      setRestoringSession(false);
      return;
    }
    if (!shouldRevalidateSession(wasEnabled, false, query.data?.user?.roles)) {
      previousMaintenance.current = false;
      return;
    }
    const generation = ++restorationGeneration.current;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let retryDelayMs = 3000;
    let cancelled = false;
    setRestoringSession(true);
    const restore = async () => {
      try {
        const data = await apiFetch<{ user: User }>("/auth/me");
        if (cancelled || restorationGeneration.current !== generation) return;
        const previousUserId = queryClient
          .getQueryData<{ user: User | null }>(AUTH_QUERY_KEY)?.user?.userId;
        clearUserQueriesForIdentityChange(queryClient, previousUserId, data.user.userId);
        queryClient.setQueryData(AUTH_QUERY_KEY, data);
        previousMaintenance.current = false;
        setRestoringSession(false);
      } catch (error) {
        if (cancelled || restorationGeneration.current !== generation) return;
        if (error instanceof Error && error.message === "unauthorized") {
          queryClient.setQueryData(AUTH_QUERY_KEY, { user: null });
          previousMaintenance.current = false;
          setRestoringSession(false);
          return;
        }
        const delay = retryDelayMs + Math.floor(Math.random() * 500);
        retryDelayMs = Math.min(30_000, retryDelayMs * 2);
        retryTimer = setTimeout(() => void restore(), delay);
      }
    };
    void restore();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [maintenanceSetting, query.data?.user?.roles, query.error?.message, queryClient]);

  const maintenanceEnabled = (maintenanceSetting ?? false)
    || query.error?.message === "maintenance_mode";
  const isRestoringSession = restoringSession || sessionRevalidationRequired;
  return (
    <AuthContext.Provider value={{
      user: query.data?.user ?? null,
      loading: query.isLoading || isRestoringSession,
      publicConfig: publicConfigQuery.data ?? null,
      publicConfigLoading: publicConfigQuery.isPending,
      maintenanceEnabled,
      restoringSession: isRestoringSession,
      refresh,
      refreshPublicConfig,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
